import { useCallback, useEffect, useRef, useState } from 'react'

import { AppLink } from '../components/AppLink'
import s from './Export.module.css'

/**
 * Put your gigs in your own calendar.
 *
 * A calendar client cannot sign in, so each URL carries a secret instead of relying on a
 * cookie. That is why the page says plainly that anyone holding one can read those gigs, and
 * why `Get new links` sits next to them rather than being buried.
 *
 * **One link that carries whatever is ticked, and a link per feed beside it.** Ticking several
 * gets them together, deduplicated, in one calendar; the separate links get them as separate
 * calendars, which is what makes them colourable and hideable one at a time.
 *
 * **Holding two of these at once doubles the gigs they share**, and every pair of these lists
 * overlaps on live data — most of them completely. A gig carries a different `UID` in each
 * feed, deliberately, so that two subscriptions never fight over one entry with whichever
 * fetched last winning; doubling is the price of that and the owner has accepted it as worth
 * paying for separate calendars.
 *
 * **The combined address never changes when the ticks do** — the choice is kept against the account,
 * not in the path — because a URL that moved every time somebody changed their mind would kill
 * the subscription already sitting in their calendar app.
 *
 * **The counts come from the feeds, never from the site's tabs.** They are different numbers:
 * an editor's Saved tab counts the curation rule, so it can read in the hundreds while the feed
 * holds nothing. A count beside a URL has to be the count of what the URL serves.
 *
 * **The gate is the server's.** A feed with nothing in it comes back with no link at all, and
 * no token is minted for somebody who has nothing to export. There is nothing here that could
 * be talked out of it from a devtools console.
 */

/**
 * `count` is everything the feed's document carries, back catalogue included, and is the only
 * number any gate reads. `upcoming` is the part of that on or after tonight's edge; it is
 * printed and gates nothing — a feed of gigs already been to is still a record worth its URL.
 */
type Feed = {
  slug: string
  count: number
  upcoming: number
  chosen: boolean
  public: boolean
  link: string | null
}
type Answer = { feeds: Feed[]; link: string | null; carrying: number; upcoming: number }

/**
 * The two numbers as one phrase, coming up first: that is the question a reader brings to the
 * page, and the total, which since the feeds kept their past is larger, would otherwise be
 * read as it. Each half is unbreakable so that if the phrase has to wrap on a narrow screen it
 * breaks at the separator and never between a number and its words.
 */
const phrase = (upcoming: number, count: number) => (
  <>
    <span className={s.half}>{upcoming} coming up</span>
    <span className={s.sep}> · </span>
    <span className={s.half}>{count} in all</span>
  </>
)

/** What each feed is called on the page, and what it is for.
 *
 * `saved` is shown as **Liked** deliberately. The slug is the feed's `UID` domain and renaming
 * it would orphan every subscription already made, so the word on the page and the word in the
 * namespace are allowed to differ — this is the only place that divergence lives. */
const SAID: Record<string, { name: string; note: string }> = {
  saved: { name: 'Liked', note: 'Gigs you have saved' },
  going: { name: 'Going', note: 'Gigs you have said you are going to' },
  friends: { name: 'Friends going', note: 'Where your friends will actually be' },
  curated: { name: 'Curated', note: 'The London Gigs calendar — public, no link needed' },
}

export function Export() {
  const [answer, setAnswer] = useState<Answer | null>(null)
  const [asked, setAsked] = useState(false)
  const [copied, setCopied] = useState('')
  const [trouble, setTrouble] = useState('')
  /*
   * **What the reader has asked for, which is not the same as what the server has confirmed.**
   * Ticks arrive faster than round trips: a second click computed from the last *answered*
   * state is computed from a base that already excludes the first, so the first tick is sent,
   * overwritten and silently lost — `choose` replaces the whole selection. Held here so the
   * next click builds on the click before it rather than on the last reply.
   */
  const wanted = useRef<string[] | null>(null)
  /*
   * And answers can arrive out of order. Without a sequence the last `setAnswer` to run wins
   * whichever request it belonged to, so the page can end up rendering one selection while the
   * combined URL serves another, until a reload. Only the newest request may paint.
   */
  const asked_at = useRef(0)

  const ask = useCallback((body: Record<string, unknown>) => {
    setTrouble('')
    /*
     * A POST even on load, because rotating mints and ticking writes — the argument
     * `/invitation` makes about credentials, and the same one applies to a choice. A GET that
     * did either would do it for every speculative fetch a browser felt like.
     */
    const mine = ++asked_at.current
    fetch('/subscription', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then(answered => (answered.ok ? answered.json() : Promise.reject(new Error('no'))))
      .then((found: Answer) => {
        if (mine !== asked_at.current) return
        wanted.current = null
        setAnswer(found)
        setAsked(true)
        setCopied('')
      })
      .catch(() => {
        /*
         * **`asked` stays false**, because it means "the server told us what this account has"
         * and a failed request did not. Setting it here rendered the empty-list sentence beside
         * the error, so an expired session in another tab told somebody with a full list to go
         * and save some gigs.
         *
         * **And `wanted` is dropped.** Clearing it only on success left a rejected tick painted
         * for ever — the box stayed ticked, the "Carrying" line named a feed the server had
         * refused, and `Try again` could not render because it is drawn only before the first
         * answer lands. Worse, the phantom rode along on the next tick that *did* succeed, so a
         * change the reader had been told failed was committed later, silently.
         */
        if (mine === asked_at.current) wanted.current = null
        setTrouble('that did not work')
      })
  }, [])

  useEffect(() => ask({}), [ask])

  const feeds = answer?.feeds ?? []
  const anything = feeds.some(feed => feed.count > 0)
  const whole = (link: string) => `${window.location.origin}${link}`

  function copy(link: string, key: string) {
    // `writeText` needs a secure context, which a phone on the local network is not. Every URL
    // is on the page anyway, so failing means "select it yourself" rather than nothing at all.
    void navigator.clipboard
      ?.writeText(whole(link))
      .then(() => setCopied(key))
      .catch(() => setTrouble('copy it from the box above'))
  }

  function tick(slug: string, on: boolean) {
    /*
     * The whole selection every time, never a toggle — so the server replaces what it holds and
     * two tabs cannot interleave into a half-state.
     *
     * **Built on the last click, not the last answer.** Reading `feed.chosen` here was the bug:
     * a second click inside one round trip computed its set from a base that still had the
     * first click's state unset, sent it, and the server's whole-set replace threw the first
     * tick away. The reader watched a box tick itself and then untick itself, with no message.
     */
    const base = wanted.current ?? feeds.filter(feed => feed.chosen).map(feed => feed.slug)
    const chosen = on ? [...base, slug] : base.filter(name => name !== slug)
    wanted.current = chosen
    ask({ choose: chosen })
  }

  /** Ticked, as the reader last asked — falling back to what the server confirmed. */
  const isTicked = (feed: Feed) =>
    wanted.current ? wanted.current.includes(feed.slug) : feed.chosen

  const ticked = feeds.filter(isTicked)
  /*
   * **The server's number, never the sum of the rows.** Every pair of these lists overlaps and
   * most overlap completely, so adding the ticked counts is not an imprecise answer but a
   * different one: ticking all four advertised 679 gigs where the document carries 362. A
   * reader who pastes that URL in and gets 362 concludes the feed drops half of what it
   * promises. `carrying` and `upcoming` are `ics.tally`'s two sums for the very feed the link
   * serves.
   */
  const carrying = answer?.carrying ?? 0
  const upcoming = answer?.upcoming ?? 0

  return (
    <main className={s.page}>
      <h1 className={s.title}>Export your gigs</h1>
      <p className={s.blurb}>
        Subscribe from any calendar app. It updates itself — save a gig here and it turns up in
        your calendar within the hour.
      </p>

      <p className={s.err}>{trouble}</p>

      {trouble && !answer ? (
        <p className={s.count}>
          <button type="button" className={s.plain} onClick={() => ask({})}>
            Try again
          </button>
        </p>
      ) : null}

      {asked && !anything ? (
        <p className={s.nothing}>
          Add events to your calendar before you can export. Save a gig from the{' '}
          <AppLink to="/calendar">calendar</AppLink> and your link will appear here.
        </p>
      ) : null}

      {answer && anything ? (
        <>
          {answer.link ? (
            <>
              <p className={s.sectionlabel}>Your calendar link</p>
              <div className={s.linkRow}>
                <input
                  className={s.link}
                  value={whole(answer.link)}
                  readOnly
                  aria-label="Your combined calendar URL"
                />
                <button type="button" className={s.copy} onClick={() => copy(answer.link!, 'all')}>
                  {copied === 'all' ? 'Copied' : 'Copy'}
                </button>
              </div>
              <p className={s.count}>
                {ticked.length ? (
                  <>
                    Carrying {ticked.map(feed => SAID[feed.slug]?.name ?? feed.slug).join(', ')}{' '}
                    · {phrase(upcoming, carrying)}
                  </>
                ) : (
                  'Nothing ticked — this link is empty until you choose something.'
                )}
              </p>
            </>
          ) : null}

          <p className={s.sectionlabel}>What it carries</p>
          <div className={s.feeds}>
            {feeds.map(feed => {
              const said = SAID[feed.slug] ?? { name: feed.slug, note: '' }
              const empty = feed.count === 0
              return (
                <div key={feed.slug} className={empty ? `${s.feed} ${s.off}` : s.feed}>
                  {/*
                    * Disabled only when it is empty *and* not already ticked. A feed that was
                    * ticked and has since emptied — a Going list whose gigs have all been and
                    * gone — would otherwise be a checked box nobody can clear, leaving an arm
                    * in the combined feed whose state the reader cannot change.
                    */}
                  <input
                    type="checkbox"
                    id={`feed-${feed.slug}`}
                    checked={isTicked(feed)}
                    disabled={empty && !isTicked(feed)}
                    onChange={event => tick(feed.slug, event.target.checked)}
                  />
                  <label htmlFor={`feed-${feed.slug}`}>
                    <span className={s.name}>{said.name}</span>
                    <span className={s.note}>
                      {empty ? 'Nothing here yet' : said.note}
                    </span>
                  </label>
                  {/*
                    * A bare zero for a feed with nothing at all: the note beside it already
                    * says so, and "0 coming up · 0 in all" would be the same fact said again.
                    * A feed with something behind it and nothing ahead prints the phrase with
                    * its zero, and its row stays live — the gate is `count`.
                    */}
                  <span className={empty ? `${s.tally} ${s.zero}` : s.tally}>
                    {empty ? feed.count : phrase(feed.upcoming, feed.count)}
                  </span>
                </div>
              )
            })}
          </div>

          {/*
            * A calendar of its own per feed, for somebody who wants Curated grey and hideable
            * beside their own gigs in blue. Held alongside the combined link these double the
            * gigs they share — every pair of these lists overlaps, most of them completely —
            * and that is a known, accepted cost rather than an oversight.
            */}
          <p className={s.sectionlabel}>Or one calendar each</p>
          {feeds
            .filter(feed => feed.link)
            .map(feed => (
              <div key={feed.slug} className={s.own}>
                <p className={s.ownlabel}>{SAID[feed.slug]?.name ?? feed.slug}</p>
                <div className={s.linkRow}>
                  <input
                    className={s.link}
                    value={whole(feed.link!)}
                    readOnly
                    aria-label={`${SAID[feed.slug]?.name ?? feed.slug} calendar URL`}
                  />
                  <button
                    type="button"
                    className={s.copy}
                    onClick={() => copy(feed.link!, feed.slug)}
                  >
                    {copied === feed.slug ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>
            ))}

          <p className={s.count}>
            Anyone with this link can see those gigs ·{' '}
            <button type="button" className={s.plain} onClick={() => ask({ rotate: true })}>
              Get a new link
            </button>
          </p>

          <section className={s.how}>
            <h2 className={s.subtitle}>Google Calendar</h2>
            <ol className={s.steps}>
              <li>
                Next to <b>Other calendars</b>, press <b>+</b>.
              </li>
              <li>
                Choose <b>From URL</b> — <span className={s.warn}>not <b>Import</b></span>, which
                takes a one-off copy that never updates.
              </li>
              <li>Paste the link and press Add calendar.</li>
            </ol>

            <h2 className={s.subtitle}>Apple Calendar</h2>
            <ol className={s.steps}>
              <li>
                Mac: <b>File</b> → <b>New Calendar Subscription</b>. iPhone: <b>Calendars</b> →{' '}
                <b>Add Calendar</b> → <b>Add Subscription Calendar</b>.
              </li>
              <li>Paste the link and subscribe.</li>
              <li>Set it to refresh every hour.</li>
            </ol>

            <h2 className={s.subtitle}>Outlook</h2>
            <ol className={s.steps}>
              <li>
                Open <b>Calendar</b> and press <b>Add calendar</b>.
              </li>
              <li>
                Choose <b>Subscribe from web</b>.
              </li>
              <li>Paste the link, name it, press Import.</li>
            </ol>
          </section>
        </>
      ) : null}
    </main>
  )
}
